import type { CSSProperties } from 'react';
import type { AvatarShape, AvatarSize } from './SuperAvatar.types';

interface SizeSpec {
  px: number;
  fontSize: number;
  family: string;
  dotSize: number;
}

const sizeMap: Record<AvatarSize, SizeSpec> = {
  sm: { px: 24, fontSize: 10, family: 'var(--font-sans)', dotSize: 8 },
  md: { px: 32, fontSize: 12, family: 'var(--font-sans)', dotSize: 11 },
  lg: { px: 56, fontSize: 18, family: 'var(--font-serif)', dotSize: 14 },
  xl: { px: 84, fontSize: 28, family: 'var(--font-serif)', dotSize: 18 },
};

export function getSizeSpec(size: AvatarSize): SizeSpec {
  return sizeMap[size];
}

export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0]?.slice(0, 2) ?? '?').toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}

/** Deterministic color from name → surface tone bucket. */
export function pickToneBg(seed: string): { bg: string; fg: string } {
  const palettes: ReadonlyArray<{ bg: string; fg: string }> = [
    { bg: 'var(--color-surface-3)', fg: 'var(--color-text)' },
    { bg: 'rgba(212,168,87,0.18)', fg: 'var(--color-accent)' },
    { bg: 'rgba(143,181,115,0.18)', fg: 'var(--color-good)' },
    { bg: 'rgba(201,122,107,0.18)', fg: 'var(--color-bad)' },
    { bg: 'rgba(127,163,184,0.18)', fg: 'var(--color-info)' },
  ];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return palettes[hash % palettes.length] ?? palettes[0]!;
}

export function buildAvatarStyle(args: {
  size: AvatarSize;
  shape: AvatarShape;
  tone: { bg: string; fg: string };
}): CSSProperties {
  const spec = sizeMap[args.size];
  return {
    width: spec.px,
    height: spec.px,
    borderRadius: args.shape === 'circle' ? '50%' : 'var(--radius-md)',
    background: args.tone.bg,
    color: args.tone.fg,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: spec.fontSize,
    fontFamily: spec.family,
    fontWeight: 400,
    flexShrink: 0,
    position: 'relative',
    overflow: 'hidden',
  };
}

export function buildChannelDotStyle(size: AvatarSize, channelColor: string): CSSProperties {
  const dot = sizeMap[size].dotSize;
  return {
    position: 'absolute',
    bottom: -1,
    right: -1,
    width: dot,
    height: dot,
    borderRadius: '50%',
    border: '2px solid var(--color-bg)',
    background: channelColor,
  };
}
